/**
 * Client-side downscale for camera photos before they're sent to the AI
 * Smart Scan endpoint or uploaded to storage. Keeps payloads small/fast on
 * mobile connections and under the backend's request-size guard.
 */

export interface ResizedImage {
  /** JPEG blob, ready to wrap in a File for upload. */
  blob: Blob;
  /** data:image/jpeg;base64,... string, ready for the Smart Scan API. */
  dataUrl: string;
  mediaType: 'image/jpeg';
}

export function resizeImageFile(file: File, maxDim = 1280, quality = 0.82): Promise<ResizedImage> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width > height) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to encode image'));
            return;
          }
          resolve({ blob, dataUrl, mediaType: 'image/jpeg' });
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });
}
